/**
 * Turns the balance's `pools` array into the rows the credits panel draws (#724):
 * every pool the user can spend from — their own, plus each team they're an active
 * member of — with the one their AI usage is billed to flagged.
 *
 * Kept out of the directive for the same reason ai-credits-refresh-gate.ts is: the
 * directive needs a DOM and a digest to exercise, while the decisions worth pinning
 * down here are pure.
 *
 * The wire types are pulled in with `import type`, so this module has no runtime
 * dependency on anything: the spec loads it on its own, with no Angular and no bundler.
 */
import type { AiCreditPool } from '#gp/app-home/services/ai-credits-contract';

/**
 * The server deliberately sends `name: null` for the personal pool (only teams have
 * names), leaving the wording to the client.
 */
export const PERSONAL_POOL_NAME = 'Your personal credits';

/**
 * Fallback for a team pool that arrives without a name. A team always has one, so
 * this is belt-and-braces — but an unlabelled row in a list about *where your money
 * goes* would be worse than a generic label.
 */
export const UNNAMED_TEAM_POOL_NAME = 'Your team';

/** One rendered pool row. Purely presentational: no formatting, no wire fields the panel doesn't use. */
export interface AiCreditPoolRow {
    /** Unique per pool (owner type + id) — the `track by` key for the repeat. */
    key: string;
    /** The team's name, or the personal pool's client-chosen label. */
    name: string;
    /** True for the user's own pool, false for a team's. */
    isPersonal: boolean;
    /** True on the single pool this user's AI usage is currently charged to. */
    isCurrent: boolean;
    /**
     * Whether this pool has an allotment at all. False means the row must say NOTHING
     * about an allotment — not "0 allotted credits" — because a pool that has never
     * been granted a plan allotment does not have an empty one (#724's Goal states
     * this explicitly).
     */
    hasAllotment: boolean;
    /**
     * Allotment credits left, never below zero. Only meaningful when
     * {@link hasAllotment}.
     *
     * Clamped because a pool really can go negative: metering posts after a stream
     * finishes, so spend can overshoot into overage. Rendered raw, the row read
     * "-1,240 allotted credits left." — a debt phrased as a positive. {@link isEmpty}
     * carries the overdrawn state instead, and it is computed from the unclamped
     * figures so an overage still counts as dry.
     */
    allotmentRemaining: number;
    /**
     * Allotment left in a pool that is out of the headline bar because nothing will
     * refill it — a personal pool whose subscription moved to a team (#1686). The row
     * says "They do not renew.", since these credits appear nowhere else on the panel.
     */
    allotmentDoesNotRenew: boolean;
    /** Whether any purchased credits have ever been added to this pool. */
    hasPurchased: boolean;
    /** Purchased credits left, out of {@link purchasedTotal}; clamped like {@link allotmentRemaining}. */
    purchasedRemaining: number;
    /** All-time purchased credits (paid top-ups + promotional grants). */
    purchasedTotal: number;
    /**
     * The pool has held credits and has none left to spend. This is the state that
     * makes the billed pool understandable when it isn't the first team in the list:
     * spend prefers a team pool that still has credits and falls through the dry ones.
     */
    isEmpty: boolean;
    /** The pool has never held credits of either kind — nothing to report but its existence. */
    isUnfunded: boolean;
}

/**
 * The part of the balance this module reads. Structural, and `pools` is optional on
 * purpose: a browser holding a page from a server that predates #724 gets a response
 * with no pools at all, and that must degrade to "no list", never to an exception.
 */
export interface AiCreditPoolListSource {
    pools?: AiCreditPool[];
}

/**
 * Map the server's pools to rows, preserving the order the server sent them in. No
 * filtering: the server already leaves out a pool with nothing left to show (#1686).
 */
export function buildPoolRows(balance: AiCreditPoolListSource | null | undefined): AiCreditPoolRow[] {
    const pools = (balance && balance.pools) || [];

    return pools.filter(pool => !!pool).map(toRow);
}

/**
 * Whether the pool list is worth drawing.
 *
 * The overwhelming majority of users have no teams, so their list would be a single
 * row restating the figures immediately above it — noise on a panel that is already
 * dense. One pool means there is no question about where usage is charged, which is
 * the only question the list exists to answer.
 * An older server's missing `pools` yields no rows and so falls out of the same rule,
 * leaving the panel exactly as it was before #724.
 */
export function shouldShowPoolList(rows: AiCreditPoolRow[]): boolean {
    return rows.length > 1;
}

/**
 * The headline figures, summed over pools rather than read from the billed one,
 * because a user can hold credits in several (#724).
 *
 * Allotment is summed only over the pools the server marks countsTowardAllotment: the
 * billed pool and any pool that renews, except that staff count only their personal
 * pool (#1686). A personal pool whose subscription moved to a team
 * used to keep its last, spent cycle in the bar for good; any allotment it still holds
 * is stated on its own row instead. Purchased credits never expire, so every listed
 * pool's are summed.
 */
export interface AiCreditPoolTotals {
    /** Allotment spent across the counted pools, in each pool's current cycle. */
    allotmentUsed: number;
    /** What those cycles made available in total — the bar's denominator. */
    allotmentTotal: number;
    /** False when no counted pool has an allotment, so the panel shows no allotment bar. */
    hasAllotment: boolean;
    /**
     * When the bar's cycle began, for "… used since {date}." Set only when exactly one
     * pool makes up the bar: pools restart on different dates, so a sum has no single
     * start. Null when the pools carry no cycle start (a server predating #1686).
     */
    allotmentCycleStartUtc: string | null;
    /** Purchased credits spent across every listed pool, all-time. */
    purchasedUsed: number;
    /** Purchased credits ever added across every listed pool. */
    purchasedTotal: number;
    /** False when nothing has ever been purchased anywhere, so no purchased bar. */
    hasPurchased: boolean;
}

/**
 * What the totals are read from. The top-level fields are the fallback: a browser
 * holding a bundle newer than its server gets a response with no `pools`, and the
 * panel must still draw the billed pool's figures rather than a row of zeros.
 */
export interface AiCreditPoolTotalsSource extends AiCreditPoolListSource {
    monthlyAllotment?: number;
    allotmentUsed?: number;
    purchasedUsed?: number;
    purchasedTotal?: number;
}

export function totalAcrossPools(balance: AiCreditPoolTotalsSource | null | undefined): AiCreditPoolTotals {
    const pools = (balance && balance.pools) || [];

    if (!pools.length) {
        // No pool list: fall back to the billed pool's own figures, which is exactly
        // what the panel showed before #724.
        const allotmentTotal = (balance && balance.monthlyAllotment) || 0;
        const purchasedTotal = (balance && balance.purchasedTotal) || 0;

        return {
            allotmentUsed: (balance && balance.allotmentUsed) || 0,
            allotmentTotal,
            hasAllotment: allotmentTotal > 0,
            allotmentCycleStartUtc: null,
            purchasedUsed: (balance && balance.purchasedUsed) || 0,
            purchasedTotal,
            hasPurchased: purchasedTotal > 0,
        };
    }

    let allotmentUsed = 0;
    let allotmentTotal = 0;
    let purchasedUsed = 0;
    let purchasedTotal = 0;

    // A pool with no allotment contributes nothing to either side rather than a zero
    // denominator, and the bar exists only if some pool does contribute — so a user
    // whose only credits are purchased still sees no empty allotment bar.
    const inBar = pools.filter(pool => !!pool && pool.hasAllotment && countsTowardAllotment(pool));

    inBar.forEach(pool => {
        allotmentUsed += pool.allotmentUsed || 0;
        allotmentTotal += pool.allotmentTotal || 0;
    });

    pools.filter(pool => !!pool).forEach(pool => {
        purchasedUsed += pool.purchasedUsed || 0;
        purchasedTotal += pool.purchasedTotal || 0;
    });

    return {
        // Clamped: a pool can be overdrawn, and neither a negative "used" nor a
        // negative denominator is a sentence the panel can render.
        allotmentUsed: Math.max(0, allotmentUsed),
        allotmentTotal: Math.max(0, allotmentTotal),
        hasAllotment: inBar.length > 0 && allotmentTotal > 0,
        allotmentCycleStartUtc: inBar.length === 1 ? inBar[0].allotmentCycleStartUtc || null : null,
        purchasedUsed: Math.max(0, purchasedUsed),
        purchasedTotal: Math.max(0, purchasedTotal),
        hasPurchased: purchasedTotal > 0,
    };
}

/** Missing means a server that predates #1686, which summed every pool. */
function countsTowardAllotment(pool: AiCreditPool): boolean {
    return pool.countsTowardAllotment !== false;
}

function toRow(pool: AiCreditPool): AiCreditPoolRow {
    // `|| 0` throughout, as the rest of this panel does: an older or partial response
    // must render as a zero figure, never as "NaN credits".
    const allotmentRemaining = pool.allotmentRemaining || 0;
    const purchasedRemaining = pool.purchasedRemaining || 0;
    const purchasedTotal = pool.purchasedTotal || 0;
    const hasAllotment = !!pool.hasAllotment;
    const hasPurchased = purchasedTotal > 0;

    // The row's "nothing left to spend" has to line up with what made spend skip this
    // pool, or the list explains the billed pool by a different rule than the one that
    // chose it. Computed from the RAW figures: an overdrawn pool must still count as
    // dry here, even though the figures the row displays are clamped at zero.
    const spendable = allotmentRemaining + purchasedRemaining;

    return {
        key: pool.ownerType + ':' + pool.ownerId,
        name: poolName(pool),
        // Anything that isn't a team is the user's own pool: an unrecognised owner type
        // should read as personal rather than silently claim to be a team's shared money.
        isPersonal: pool.ownerType !== 'team',
        isCurrent: !!pool.isCurrent,
        hasAllotment,
        // Never below zero — see the field docs above.
        allotmentRemaining: Math.max(0, allotmentRemaining),
        // Both flags tested for an explicit false: absent, from a server that predates
        // #1686, the row reads as it always did. A staff member's team pool is out of
        // the bar but does renew, so it gets no such sentence.
        allotmentDoesNotRenew: hasAllotment && !countsTowardAllotment(pool) && pool.allotmentRenews === false,
        hasPurchased,
        purchasedRemaining: Math.max(0, purchasedRemaining),
        purchasedTotal,
        isEmpty: (hasAllotment || hasPurchased) && spendable <= 0,
        isUnfunded: !hasAllotment && !hasPurchased,
    };
}

function poolName(pool: AiCreditPool): string {
    if (pool.ownerType !== 'team') return PERSONAL_POOL_NAME;

    return (pool.name || '').trim() || UNNAMED_TEAM_POOL_NAME;
}
